import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plane, Loader2 } from "lucide-react";
import { HERO_IMAGE } from "@/data/content";
import { BrandWord, GoogleIcon } from "@/components/BrandWord";
import { toast } from "sonner";

export default function Login() {
  const { login, user, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/dashboard");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-brand-navy">
      <div className="hidden lg:block relative">
        <img src={HERO_IMAGE} alt="Travel" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-display text-4xl text-brand-text leading-tight">Your passage to the world begins here.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2.5 mb-10" data-testid="login-logo">
            <span className="w-9 h-9 rounded-full bg-brand-gold/15 border border-brand-gold/40 flex items-center justify-center">
              <Plane className="w-4 h-4 text-brand-gold" />
            </span>
            <BrandWord className="text-lg" />
          </Link>

          <h1 className="font-display text-3xl text-brand-text mb-2">Sign in</h1>
          <p className="text-white/50 mb-8 text-sm">Access your travel portal.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-white/70">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input" className="mt-2 bg-brand-surface border-white/10 text-white h-11" placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-white/70">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input" className="mt-2 bg-brand-surface border-white/10 text-white h-11" placeholder="••••••••" />
            </div>
            {error && <p className="text-red-400 text-sm" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full h-11 rounded-full bg-brand-gold text-brand-navy hover:bg-brand-goldhover font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-brand-navy px-3 text-white/40">or</span></div>
          </div>

          <Button type="button" onClick={googleLogin} data-testid="google-login-btn" variant="outline"
            className="w-full h-11 rounded-full border-white/15 bg-white text-brand-navy hover:bg-white/90 font-semibold">
            <GoogleIcon /> Continue with Google
          </Button>

          <p className="text-white/50 text-sm mt-6 text-center">
            No account? <Link to="/register" className="text-brand-gold hover:underline" data-testid="login-to-register">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
